/* eslint-disable max-lines */
import _ from 'lodash';
import TeamAvatar from 'components/base/TeamAvatar';
import React from 'react';
import QueryString from 'query-string';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import {
  CONTRIBUTOR_ROLES,
  LOCAL_STORAGE_KEYS,
  TRACKING,
  TEAM_TYPES
} from 'common/constants/constants';
import Text, { Inline } from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import WorkBreakdownProgress from 'components/WorkBreakdown/WorkBeakdownProgress';
import { isGroup, numeralify, parseResponseMsg } from 'common/utils/utils';
import {
  getDurationLevels,
  getDefaultFormatter
} from 'common/utils/time.utils';
import AppRouter from 'common/AppRouter';
import { getColor } from 'common/constants/_colors';
import {
  teamClicked,
  teamCreatedSuccessfully,
  teamEditedSuccessfully
} from 'common/constants/messages';
import * as TrackingService from 'common/services/tracking.service';
import { CycleTimePopupTooltip } from 'components/Tooltips/CycleTimeTooltip';
import {
  calcWorkBreakDownData,
  getWorkBreakdownConfigurations
} from '../../../dashboard/util';
import { getCycleTimeConfigurations } from 'views/root/views/home/views/dashboard/dashboard.service';
import { mapCycleTimeStatisticsToPipelineMilestones } from 'components/DeliveryPipeline';
import {
  ACCOUNT_TYPES_MAP,
  MEDIAN_METRICS,
  metricNames,
  BENCHMARK_ORG_SIZE_KEYS
} from '@linearb/linjs-common';
import { WorkBreakdownPopupTooltip } from 'components/Tooltips/WorkBreakdownTooltip';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import ActionsMenu from './ActionsMenu';

const InviteLink = styled(Link)`
  color: ${getColor('white')};
`;

const LinkWithoutUnderline = styled(Link)`
  text-decoration: none;
  color: ${getColor('black')};
`;

export const getColumns = ({ account = {}, enableSort = true }) => [
  {
    dataPoint: 'team',
    name: '',
    isSortedCol: false,
    tableCellProps: {
      style: {
        minWidth: '10rem'
      }
    }
  },
  {
    dataPoint: metricNames.ACTIVE_BRANCHES,
    name: 'WIP',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderRight: 0 } }
  },
  {
    dataPoint: metricNames.DONE_BRANCHES,
    name: 'DONE',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.ACTIVE_DAYS,
    name: 'ACTIVE DAYS',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.COMMIT_CODE_CHANGES,
    name: 'CODE CHANGES',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderRight: 0, width: '9%' } }
  },
  {
    dataPoint: metricNames.COMMIT_INVOLVED_REPOS_COUNT,
    name: 'REPOSITORIES',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.CYCLE_TIME,
    name: 'CYCLE TIME',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: 'workBreakdown',
    name: 'WORK BREAKDOWN',
    isSortedCol: false,
    tableCellProps: { style: { borderLeft: 0, minWidth: '9rem' } }
  },
  {
    dataPoint: metricNames.PR_NEW,
    name: 'PRs OPENED',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderRight: 0 } }
  },
  {
    dataPoint: metricNames.REVIEWS,
    name: 'REVIEWS',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderRight: 0, borderLeft: 0 } }
  },
  {
    dataPoint: metricNames.REVIEW_DEPTH,
    name: 'REVIEW DEPTH',
    isSortedCol: enableSort,
    tableCellProps: { style: { borderLeft: 0 } }
  },
  ...(account.type_id === ACCOUNT_TYPES_MAP.Viewer
    ? []
    : [
        {
          dataPoint: 'actions',
          name: '',
          isSortedCol: false
        }
      ])
];

const enabledTeamLinkStyle = {
  textDecoration: 'none',
  color: `${getColor('black')}`
};

export const disabledTeamLinkStyle = {
  ...enabledTeamLinkStyle,
  pointerEvents: 'none'
};

export const getTeamsTableData = ({
  data,
  time: { from, to },
  onEditTeamClick,
  onDeleteTeamClick,
  isTeamLicense,
  toggleSnackBar,
  disableTeamLink = false,
  metricsCalculationConfig = {},
  benchmarkOrgSize = BENCHMARK_ORG_SIZE_KEYS.all,
  onEditGroupClick,
  onDeleteGroupClick,
  accounts
}) => {
  const { metricsCalculationStrategy } = metricsCalculationConfig;

  const localStorageAccessor = BrowserStorageAccessor().local;
  return data.map(curItem => {
    MEDIAN_METRICS.forEach(medianMetric => {
      curItem[medianMetric] =
        curItem[`${medianMetric}:${metricsCalculationStrategy}`];
    });

    const {
      team_id,
      [metricNames.ACTIVE_BRANCHES]: activeBranchesCount = '-',
      [metricNames.DONE_BRANCHES]: doneBranchesCount = '-',
      [metricNames.REVIEWS]: pullRequestReviewCount = '-',
      [metricNames.REVIEW_DEPTH]: pullRequestReviewDepth = null,
      [metricNames.CYCLE_TIME]: cycleTime,
      [metricNames.ACTIVE_DAYS]: activeDay = '-',
      [metricNames.COMMIT_CODE_CHANGES]: changes = '-',
      [metricNames.COMMIT_INVOLVED_REPOS_COUNT]: involvedRepos = '-',
      [metricNames.NEW_WORK]: commitNewWork = 0,
      [metricNames.REWORK]: commitRework = 0,
      [metricNames.REFACTOR]: commitRefactor = 0,
      [metricNames.PR_NEW]: prNew = '-',

      name
    } = curItem;

    const cycleTimeDisplay = cycleTime
      ? {
          value: getDurationLevels({
            minutes: cycleTime || 0,
            format: getDefaultFormatter({ minutes: cycleTime || 0 }),
            ignoreZeros: true
          })
        }
      : {};

    const search = QueryString.parse(window.location.search, {
      ignoreQueryPrefix: true
    });

    return {
      team: (
        <Link
          style={disableTeamLink ? disabledTeamLinkStyle : enabledTeamLinkStyle}
          onClick={() => {
            TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
              page: TRACKING.PAGES.PEOPLE,
              page_tab: TRACKING.PAGE_TABS.TEAMS,
              click_type: TRACKING.CLICK.OPEN_TEAM
            });
            if (!localStorageAccessor.get(LOCAL_STORAGE_KEYS.CLICKED_ON_TEAM)) {
              localStorageAccessor.set(
                LOCAL_STORAGE_KEYS.CLICKED_ON_TEAM,
                new Date()
              );
              toggleSnackBar({
                message: teamClickedMessage,
                variant: 'info'
              });
            }
          }}
          to={{
            pathname: '/contributors/individuals',
            search: QueryString.stringify({
              ...search,
              teams: team_id
            })
          }}
        >
          <Tooltip title={`View ${name} members`}>
            <Flex direction='row' items='center'>
              <TeamAvatar team={curItem} />
              <Inline
                size='large'
                pl='1rem'
                weight={400}
                multiLineEllipsisAfter={2}
              >
                {name}
              </Inline>
            </Flex>
          </Tooltip>
        </Link>
      ),
      [metricNames.ACTIVE_BRANCHES]: (
        <Text size='large'> {activeBranchesCount} </Text>
      ),
      [metricNames.DONE_BRANCHES]: (
        <Text size='large'> {doneBranchesCount} </Text>
      ),
      [metricNames.CYCLE_TIME]: (
        <CycleTimePopupTooltip
          metricsCalculationConfig={metricsCalculationConfig}
          title={
            <Text weight='500' size='x-large'>
              {'Cycle time breakdown'}
            </Text>
          }
          scale={0.9}
          configurations={getCycleTimeConfigurations(
            mapCycleTimeStatisticsToPipelineMilestones(
              curItem,
              {},
              metricsCalculationConfig,
              benchmarkOrgSize
            ),
            true
          )}
          totalTime={
            _.get(curItem, metricNames.REVIEW_TIME, null) !== null
              ? getDurationLevels({
                  minutes: cycleTime,
                  ignoreZeros: true,
                  format: getDefaultFormatter({
                    minutes: cycleTime,
                    minutesUnit: ' minutes',
                    daysUnit: ' days',
                    hoursUnit: ' hours',
                    yearsUnit: ' years'
                  }),
                  joiner: ' '
                })
              : 'N/A'
          }
        >
          <LinkWithoutUnderline
            to={{
              pathname: AppRouter.BRANCH.path,
              search: QueryString.stringify({
                iteration_id: search.iteration_id,
                teams: team_id,
                cycle_time_updated: `${from},${to}`,
                metric_name: metricNames.CYCLE_TIME,
                order_by: 'cycle_time,desc'
              })
            }}
          >
            <Text cursor='pointer' size='large'>
              {' '}
              {_.get(cycleTimeDisplay, 'value', '-')}{' '}
            </Text>
          </LinkWithoutUnderline>
        </CycleTimePopupTooltip>
      ),
      [metricNames.REVIEWS]: (
        <Text size='large'> {pullRequestReviewCount} </Text>
      ),
      [metricNames.REVIEW_DEPTH]: (
        <Text size='large'>
          {' '}
          {pullRequestReviewDepth !== null
            ? Math.ceil(pullRequestReviewDepth * 10) / 10
            : '-'}{' '}
        </Text>
      ),
      [metricNames.ACTIVE_DAYS]: <Text size='large'> {activeDay} </Text>,
      [metricNames.COMMIT_CODE_CHANGES]: (
        <Text size='large'> {numeralify(changes)} </Text>
      ),
      [metricNames.COMMIT_INVOLVED_REPOS_COUNT]: (
        <Text size='large'> {involvedRepos} </Text>
      ),
      [metricNames.PR_NEW]: (
        <LinkWithoutUnderline
          to={{
            pathname: AppRouter.PULL_REQUESTS.path,
            search: QueryString.stringify({
              iteration_id: search.iteration_id,
              teams: team_id,
              created: `${from},${to}`,
              used_in_metrics_calculation: true
            })
          }}
        >
          <Text cursor='pointer' size='large'>
            {' '}
            {prNew}{' '}
          </Text>
        </LinkWithoutUnderline>
      ),
      workBreakdown: (
        <WorkBreakdownPopupTooltip
          title={
            <Text weight='500' size='x-x-large'>
              Work breakdown
            </Text>
          }
          scale={0.75}
          configurations={getWorkBreakdownConfigurations(
            calcWorkBreakDownData({
              newWork: commitNewWork,
              rework: commitRework,
              refactor: commitRefactor,
              changes
            })
          )}
        >
          <Padding b='small'>
            <WorkBreakdownProgress
              newWork={commitNewWork}
              refactor={commitRefactor}
              rework={commitRework}
              changes={changes}
            />
          </Padding>
        </WorkBreakdownPopupTooltip>
      ),
      actions: (
        <ActionsMenu
          team={curItem}
          isTeamLicense={isTeamLicense}
          onEdit={() =>
            isGroup(curItem)
              ? onEditGroupClick({ ...curItem, id: team_id })
              : onEditTeamClick({ ...curItem, id: team_id })
          }
          onDelete={() =>
            isGroup(curItem)
              ? onDeleteGroupClick({ ...curItem, id: team_id }, accounts)
              : onDeleteTeamClick({ ...curItem, id: team_id }, accounts)
          }
          isGroup={isGroup(curItem)}
        />
      )
    };
  });
};

const teamCreatedToastMessage = (
  <Flex direction='column'>
    <Text size='large' color='white'>
      {teamCreatedSuccessfully}
      <InviteLink
        to={{
          pathname: '/settings/users',
          search: window.location.search
        }}
      >
        Click Here
      </InviteLink>
    </Text>
  </Flex>
);

const teamEditedToastMessage = (
  <Flex direction='column'>
    <Text size='large' color='white'>
      {teamEditedSuccessfully}
    </Text>
  </Flex>
);

export const teamClickedMessage = (
  <Flex direction='column'>
    <Text size='large' color='white'>
      {teamClicked}
    </Text>
  </Flex>
);

export const getTeamAvatar = ({ initials: name, color }) => ({
  name,
  color,
  withContent: true
});

export const getOnEditTeam =
  ({
    updateTeam,
    selectedTeam,
    updateTeamContributors,
    getTeams,
    toggleModal,
    toggleSnackBar = () => null
  }) =>
  async ({
    name,
    initials,
    color,
    with_history,
    parent_id,
    contributorsToAdd,
    contributorsToRemove
  }) => {
    try {
      await updateTeam({
        ...selectedTeam,
        with_history: false,
        name,
        initials,
        color,
        ...(parent_id && parent_id !== -1 ? { parent_id } : {}),
        ...(parent_id === -1 ? { delete_parent_id: true, parent_id: null } : {})
      });

      toggleModal();

      /**
       * Only update the contributors if there is something to update
       */
      if (
        (contributorsToRemove && contributorsToRemove.length) ||
        (contributorsToAdd && contributorsToAdd.length)
      ) {
        const updateContributorsSucceed = await updateTeamContributors(
          selectedTeam.id,
          contributorsToAdd.map(id => ({
            id,
            roles: [CONTRIBUTOR_ROLES.CONTRIBUTOR]
          })),
          contributorsToRemove,
          with_history
        );
        if (!updateContributorsSucceed) throw new Error();

        await getTeams();
      }

      toggleSnackBar({
        message: teamEditedToastMessage,
        variant: 'success'
      });
    } catch (e) {
      const errMsg = parseResponseMsg(e);

      toggleSnackBar({
        message: (
          <Flex direction='column'>
            <Text size='large' color='white'>
              {errMsg}
            </Text>
          </Flex>
        ),
        variant: 'error'
      });
    }
  };

export const onCreateTeam =
  ({
    createTeam,
    toggleModal,
    toggleSnackBar,
    selectTeamId,
    getTeams = Promise.resolve()
  }) =>
  async ({
    name,
    initials,
    color,
    contributor_ids,
    with_history,
    parent_id
  }) => {
    try {
      const team = await createTeam({
        name,
        initials,
        with_history,
        contributors: contributor_ids.map(individualId => ({
          id: individualId,
          roles: [CONTRIBUTOR_ROLES.CONTRIBUTOR]
        })),
        color,
        type: TEAM_TYPES.STANDARD,
        ...(parent_id && parent_id !== -1 ? { parent_id } : {})
      });
      selectTeamId && selectTeamId(team.id);

      toggleModal();

      toggleSnackBar({
        message: teamCreatedToastMessage,
        variant: 'success'
      });

      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.TEAMS,
        click_type: TRACKING.CLICK.CREATE_TEAM
      });
      await getTeams();
    } catch (e) {
      const errMsg = parseResponseMsg(e);

      toggleSnackBar({
        message: (
          <Flex direction='column'>
            <Text size='large' color='white'>
              {errMsg}
            </Text>
          </Flex>
        ),
        variant: 'error'
      });
    }
  };

export const onCreateGroup =
  ({
    createGroup,
    toggleModal,
    toggleSnackBar,
    selectTeamId,
    getTeams = Promise.resolve()
  }) =>
  async ({
    name,
    initials,
    color,
    contributor_ids,
    with_history,
    teams_ids,
    parent_id,
    type
  }) => {
    try {
      const team = await createGroup({
        name,
        initials,
        with_history,
        contributors: contributor_ids.map(individualId => ({
          id: individualId,
          roles: [CONTRIBUTOR_ROLES.CONTRIBUTOR]
        })),
        child_teams: teams_ids.map(teamId => ({
          id: teamId
        })),
        color,
        type,
        ...(parent_id && parent_id !== -1 ? { parent_id } : {})
      });

      selectTeamId && selectTeamId(team.id);

      toggleModal();

      toggleSnackBar({
        message: teamCreatedToastMessage,
        variant: 'success'
      });
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.TEAMS,
        click_type: TRACKING.CLICK.CREATE_GROUP
      });
      await getTeams();
    } catch (e) {
      const errMsg = parseResponseMsg(e);

      toggleSnackBar({
        message: (
          <Flex direction='column'>
            <Text size='large' color='white'>
              {errMsg}
            </Text>
          </Flex>
        ),
        variant: 'error'
      });
    }
  };

export const getOnEditGroup =
  ({
    updateGroup,
    selectedTeam,
    updateGroupContributors,
    getTeams,
    toggleModal,
    toggleSnackBar = () => null
  }) =>
  async ({
    name,
    initials,
    color,
    with_history,
    parent_id,
    contributorsToAdd,
    contributorsToRemove,
    teams_ids
  }) => {
    try {
      await updateGroup({
        ...selectedTeam,
        with_history,
        name,
        initials,
        color,
        child_teams: teams_ids.map(teamId => ({
          id: teamId
        })),
        ...(parent_id && parent_id !== -1 ? { parent_id } : {}),
        ...(parent_id === -1 ? { delete_parent_id: true, parent_id: null } : {})
      });

      /**
       * Only update the contributors if there is something to update
       */
      if (
        (contributorsToRemove && contributorsToRemove.length) ||
        (contributorsToAdd && contributorsToAdd.length)
      ) {
        const backfill_contributors_only = true;
        await updateGroupContributors(
          selectedTeam.id,
          contributorsToAdd.map(id => ({
            id,
            roles: [CONTRIBUTOR_ROLES.CONTRIBUTOR]
          })),
          contributorsToRemove,
          with_history,
          backfill_contributors_only
        );
      }
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.TEAMS,
        click_type: TRACKING.CLICK.UPDATE_GROUP
      });
      await getTeams();
      toggleModal();

      toggleSnackBar({
        message: teamEditedToastMessage,
        variant: 'success'
      });
    } catch (e) {
      const errMsg = parseResponseMsg(e);

      toggleSnackBar({
        message: (
          <Flex direction='column'>
            <Text size='large' color='white'>
              {errMsg}
            </Text>
          </Flex>
        ),
        variant: 'error'
      });
    }
  };
